import { Fragment, useEffect, useMemo, useState } from 'react';
import AOS from 'aos';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import placeholderImage from '../images/batiment.JPG';
import { Link } from 'react-router-dom';
import useAos from '../hooks/useAos';
import { PageMeta } from '../seo/PageMeta';
import { useRealisations } from '../hooks/useRealisations';

const GROUP_SIZE = 4;

const CATEGORY_VALUES = ['', 'building', 'industrial', 'municipal'];

function RealisationCard({ realisation, layoutClass, aosDelay = 0 }) {
    const { t } = useTranslation('common');
    const miniature = realisation.miniatures[0] ?? realisation.images[0] ?? placeholderImage;

    return (
        <div data-aos="fade-up" data-aos-delay={aosDelay}>
            <article className={`realisation-card ${layoutClass}`}>
                <Link to={`/realisation/${realisation.id}`} className="realisation-card__inner block h-full text-deco-none">
                    <img
                        src={miniature}
                        alt={realisation.title}
                        className="realisation-card__image"
                    />
                    <div className="realisation-card__footer">
                        <p className="realisation-card__title text-subtitle fustat text-regular t-white m-0">
                            {realisation.title}
                        </p>
                        <span className="realisation-card__cta btn-light-white">
                            {t('buttons.learnMore')}
                        </span>
                    </div>
                </Link>
            </article>
        </div>
    );
}

function Realisation() {
    const { t } = useTranslation(['pages', 'common']);
    const realisations = useRealisations();
    const [activeCategory, setActiveCategory] = useState(CATEGORY_VALUES[0]);

    const categories = CATEGORY_VALUES.map((value) => ({
        value,
        label: value ? t(`common:categories.${value}`) : t('common:categories.all'),
    }));

    const filteredRealisations = useMemo(
        () => activeCategory
            ? realisations.filter((realisation) => realisation.categorie === activeCategory)
            : realisations,
        [activeCategory, realisations]
    );

    const groups = useMemo(() => {
        const result = [];

        for (let index = 0; index < filteredRealisations.length; index += GROUP_SIZE) {
            result.push(filteredRealisations.slice(index, index + GROUP_SIZE));
        }

        return result;
    }, [filteredRealisations]);

    useEffect(() => {
        AOS.refresh();
    }, [activeCategory, groups.length]);

    return (
        <div className="flex flex-column items-center justify-center px-64 pt-64 pb-64 overflow-hidden w-full" data-navbar-theme="light">
            <div className="overflow-hidden w-full justify-center items-center flex">
                <p className="text-title2 w-50 text-center t-graymain fustat text-semibold pt-64 mb-0 mt-16 mx-auto" data-aos="fade-up">{t('pages:projects.heading')}</p>
            </div>

            <div className="realisation-filters flex gap-32 mt-64 items-center" data-aos="fade-up" data-aos-delay="150">
                {categories.map((category, index) => (
                    <Fragment key={category.value || 'all'}>
                        {index > 0 && (
                            <div className="realisation-filters__separator" aria-hidden="true" />
                        )}
                        <button
                            type="button"
                            className={`realisation-filter text-subtitle2 t-graymain outfit my-0 ${
                                activeCategory === category.value
                                    ? 'realisation-filter--active text-semilight'
                                    : 'text-light'
                            }`}
                            onClick={() => setActiveCategory(category.value)}
                            aria-pressed={activeCategory === category.value}
                        >
                            {category.label}
                        </button>
                    </Fragment>
                ))}
            </div>

            <div className="realisation-gallery w-full mt-64" key={activeCategory || 'all'}>
                {groups.length === 0 ? (
                    <p className="text-subtitle2 outfit text-center t-graymain my-64">{t('common:noProjectsInCategory')}</p>
                ) : (
                    groups.map((group, groupIndex) => (
                        <div key={`${activeCategory}-${group[0]?.id ?? groupIndex}`} className="realisation-gallery__group">
                            {group[0] && (
                                <RealisationCard
                                    realisation={group[0]}
                                    layoutClass="realisation-card--wide"
                                    aosDelay={groupIndex * 100}
                                />
                            )}

                            {(group[1] || group[2]) && (
                                <div className="realisation-gallery__pair">
                                    {group[1] && (
                                        <RealisationCard
                                            realisation={group[1]}
                                            layoutClass="realisation-card--left-tall"
                                            aosDelay={groupIndex * 100 + 50}
                                        />
                                    )}
                                    {group[2] && (
                                        <RealisationCard
                                            realisation={group[2]}
                                            layoutClass="realisation-card--right-offset"
                                            aosDelay={groupIndex * 100 + 100}
                                        />
                                    )}
                                </div>
                            )}

                            {group[3] && (
                                <RealisationCard
                                    realisation={group[3]}
                                    layoutClass="realisation-card--wide-right"
                                    aosDelay={groupIndex * 100 + 150}
                                />
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default function RealisationPage() {
    useAos();
    const { t } = useTranslation('pages');

    return (
        <>
            <PageMeta
                title={t('meta.projects.title')}
                description={t('meta.projects.description')}
                path="/realisations"
            />
            <Navbar />
            <Realisation />
            <Footer />
        </>
    );
}
